import {
  QUARTERLY_OKR_BRIEF,
  QUARTERLY_OKR_LIVE_REVIEW,
  QUARTERLY_OKR_REVIEW,
  QUARTERLY_OKR_SCORECARD,
  QUARTERLY_OKR_SUMMARY,
} from "../../../../data/RouteUrls";
import {
  REVIEW_SENT_STATES,
  REVIEWABLE_STATES,
  RUN_STATE,
  SUMMARY_STATES,
} from "./quarterlyOkrConfig";

// Where a quarter opens for the coach, by its run state — or `null` while the
// company is still planning and nothing has been sent yet.
//
// A run already in the live review goes STRAIGHT there: the review page's
// "Start live review" would be a second start on a running session.
export const routeForQuarter = ({
  quarter_company_id: qcId,
  run_state: runState,
  not_completed: notCompleted,
}) => {
  // ⚠️ LOCKED → THE SCORECARD (2026-10-21, by instruction), not the summary:
  // a locked quarter is being tracked, and the coach should see the same
  // board the team updates. Frozen / archived / not completed keep the
  // summary — there is nothing to track.
  const path =
    runState === RUN_STATE.LIVE_REVIEW_IN_PROGRESS
      ? QUARTERLY_OKR_LIVE_REVIEW
      : runState === RUN_STATE.LOCKED && !notCompleted
        ? QUARTERLY_OKR_SCORECARD
        : notCompleted || SUMMARY_STATES.includes(runState)
          ? QUARTERLY_OKR_SUMMARY
          : REVIEWABLE_STATES.includes(runState) ||
              REVIEW_SENT_STATES.includes(runState)
            ? QUARTERLY_OKR_REVIEW
            : null;
  return path ? path.replace(":quarterCompanyId", qcId) : null;
};

// The dashboard CTA wording for the same states.
export const ctaForQuarter = ({ run_state: runState, not_completed }) => {
  if (runState === RUN_STATE.LIVE_REVIEW_IN_PROGRESS) return "Join live review";
  if (runState === RUN_STATE.LOCKED && !not_completed) return "View scorecard";
  if (not_completed || SUMMARY_STATES.includes(runState)) return "View summary";
  if (runState === RUN_STATE.SUBMITTED_FOR_COACH) return "Start review";
  if (runState === RUN_STATE.COACH_REVIEW_IN_PROGRESS) return "Continue review";
  // Booked: the next step is the session itself. The route is the review,
  // whose footer carries "Start Live Review" (§9.5).
  if (runState === RUN_STATE.LIVE_REVIEW_SCHEDULED) return "Start live review";
  if (REVIEW_SENT_STATES.includes(runState)) return "View review";
  return "View OKRs";
};

// ─── Coach brief (handoff 34) ───────────────────────────────────────────────
export const briefRoute = (qcId) =>
  QUARTERLY_OKR_BRIEF.replace(":quarterCompanyId", qcId);

// Like the Strategy Canvas: while the quarter has just reached the coach and
// a brief exists (any status — it never blocks), the dashboard opens the
// brief, and the brief's header button goes on to the review. Once the coach
// has started the review (COACH_REVIEW_IN_PROGRESS) the button is "Continue
// review" straight into it (2026-10-23, by instruction); later states keep
// their own destination. The brief chip still links to it in every state.
export const opensOnBrief = (quarter) =>
  Boolean(quarter?.brief_status) &&
  quarter.run_state === RUN_STATE.SUBMITTED_FOR_COACH;

export const dashboardRouteFor = (quarter) =>
  opensOnBrief(quarter)
    ? briefRoute(quarter.quarter_company_id)
    : routeForQuarter(quarter);

export const dashboardCtaFor = (quarter) =>
  opensOnBrief(quarter) ? "View brief" : ctaForQuarter(quarter);

// The dashboard row for a quarter, from the cached dashboard (§9.1).
export const quarterRowOf = (clients, qcId) => {
  for (const client of clients || []) {
    const row = (client.quarters || []).find(
      (q) => String(q.quarter_company_id) === String(qcId),
    );
    if (row) return row;
  }
  return null;
};

// Crumbs under a quarter with a coach brief — the Strategy Canvas pattern:
// Quarterly OKRs › <company, links to the brief> › <this screen>. Without a
// brief, the screen's own two-step crumbs.
export const quarterCrumbs = ({
  root,
  qcId,
  row,
  companyName,
  title,
  fallbackTitle,
}) =>
  row?.brief_status
    ? [
        root,
        { title: companyName || "Brief", url: briefRoute(qcId) },
        { title, url: "" },
      ]
    : [root, { title: companyName || fallbackTitle, url: "" }];
